import type { OthersEvent } from "@liveblocks/client"
import { useIsInsideRoom, useOthers } from "@liveblocks/react"
import { groupBy } from "@prismicio/editor-support/Map"
import { Box, ErrorBoundary, Portal } from "@prismicio/editor-ui"
import { Suspense, useEffect, useRef, useState } from "react"

import { useCollaborationDataFlag } from "../../app/integrations/AmplitudeExperiments"
import { AvatarStack } from "./AvatarStack"
import { useFilterOutSuperAdmins } from "./utils"

export function CollaborationFieldLevelIndicator() {
  const isInsideRoom = useIsInsideRoom()
  const canUseCollaborationData = useCollaborationDataFlag() === "on"

  if (!isInsideRoom || !canUseCollaborationData) return null

  return (
    <ErrorBoundary>
      <Suspense>
        <FieldLevelIndicators />
      </Suspense>
    </ErrorBoundary>
  )
}

function FieldLevelIndicators() {
  const others = useOthers()
  const othersWithoutSuperAdmins = useFilterOutSuperAdmins(others)

  const groupedOthers = Array.from(
    groupBy(
      othersWithoutSuperAdmins,
      (item) => item.presence?.["fieldId"] ?? "missing-field-id",
    ).entries(),
  )

  return (
    <>
      {groupedOthers.map(([fieldId, others]) => {
        if (fieldId === "missing-field-id" || typeof fieldId !== "string")
          return null

        return (
          <Portal
            key={fieldId}
            container={document.querySelector(
              // escape the fieldId because of non-compliant format
              // example: slices[hero$some-uuid].name
              `#${CSS.escape(`portal-mount-${fieldId}`)}`,
            )}
            asChild
          >
            <PresenceIndicator others={others} />
          </Portal>
        )
      })}
    </>
  )
}

function PresenceIndicator(props: { others: OthersEvent["others"] }) {
  const { others } = props

  const [width, setWidth] = useState(0)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ref.current) {
      setWidth(ref.current.getBoundingClientRect().width)
    }
  }, [others.length])

  const addToWidth = others.length >= 2 ? 2 : 5
  return (
    <Box position="absolute" left={`-${width + addToWidth}px`} ref={ref}>
      <AvatarStack
        others={others}
        avatarSize={24}
        maximumAmountOfShownAvatars={others.length > 2 ? 1 : 2}
        overlapMargin="-12"
        showHiddenUsersAs="tooltip"
      />
    </Box>
  )
}
